import { computed, defineComponent, h, onUnmounted, ref, unref, watch } from "@vue/composition-api";
import { RateLimiter } from "limiter";
import { WebRTCConnection } from "@scrile/api-provider/dist/api/WebRTCProvider";
import { StreamingHelper } from "@scrile/streaming-client";
import providers from "@/lib/api";
import { parseISO } from "@/lib/time";
import { LoggerContextWithMessage } from "@/modules/callsLogger";
import useLiveChat from "../../hooks/useLiveChat";
import useStreamLogger from "../../hooks/useStreamLogger";
import StreamView from "../StreamView";

function initDebugInfo(WebRTC: StreamingHelper) {
  const parent = document.getElementsByClassName("go-live__wrapper-video")[0];
  const video = parent?.getElementsByTagName("video")[0];

  if (!parent || !video) {
    setTimeout(() => initDebugInfo(WebRTC), 1000);
    return;
  }

  let logContainer = document.getElementById("video_stats");
  if (!logContainer) {
    logContainer = document.createElement("div");
    logContainer.id = "video_stats";
    (logContainer.style as any) = "display: none; z-index: 10000; pointer-events: none; font-size: 5px; overflow: scroll; white-space: pre; color: white; position: absolute; padding-left: 5px; padding-bottom: 75px; left: 0; bottom: 0; width: 100%; height: 200px";
    parent.appendChild(logContainer);
  }

  let statsContainer = document.getElementById("webrtc_stats");
  if (!statsContainer) {
    statsContainer = document.createElement("div");
    statsContainer.id = "webrtc_stats";
    (statsContainer.style as any) = "display: none; z-index: 10000; pointer-events: none; font-size: 5px; overflow: scroll; white-space: pre; color: white; position: absolute; padding-left: 5px; padding-top: 55px; left: 0; top: 0; width: 100%";
    parent.appendChild(statsContainer);
  }

  function log(msg: string) {
    if (!logContainer) return;
    logContainer.innerText += msg + "\n";
  }

  function updateStats() {
    if (!statsContainer) return;
    statsContainer.innerText = WebRTC.getStatisticHistory(1)
      .consumer.rtcStatsChanges[0]?.stats.map((s: any) => JSON.stringify(s))
      .join(",\n");

    setTimeout(updateStats, 1000);
  }

  video.addEventListener("ended", () => log("ended"));
  video.addEventListener("abort", () => log("abort"));
  video.addEventListener("error", () => log("error"));
  video.addEventListener("pause", () => log("pause"));
  video.addEventListener("play", () => log("play"));
  video.addEventListener("playing", () => log("playing"));
  video.addEventListener("suspend", () => log("suspend"));

  updateStats();

  let debugVisible = false;
  let limiter = new RateLimiter({ tokensPerInterval: 10, interval: 3000 });
  document.body.addEventListener("click", () => {
    if (!limiter.tryRemoveTokens(1)) {
      if (!debugVisible) {
        logContainer && (logContainer.style.display = "block");
        statsContainer && (statsContainer.style.display = "block");
      } else {
        logContainer && (logContainer.style.display = "none");
        statsContainer && (statsContainer.style.display = "none");
      }
      debugVisible = !debugVisible;
      limiter = new RateLimiter({ tokensPerInterval: 10, interval: 3000 });
    }
  });
}

export default defineComponent({
  name: "ConsumeController",
  props: {
    userId: { type: String, required: true },
  },
  setup(props, { listeners, emit }) {
    const stream = ref<MediaStream>(new MediaStream([]));
    const connecting = ref(false);
    const connectingState = ref("");
    const connectionData = ref<WebRTCConnection | null>(null);
    const WebRTC = new StreamingHelper();

    initDebugInfo(WebRTC);

    const {
      loading,
      token,
      userList,
      joinData,
      setOnUserBroadcastingCB,
      clientServerTimeDiff,
      errorData,
    } = useLiveChat({
      userId: props.userId,
    });

    const getLoggerContext = (): Record<string, any> => {
      return {
        stream: { id: stream.value.id, active: stream.value.active },
        connection: connectionData.value,
        joinData: joinData.value,
        errorData: errorData.value,
      };
    };
    const { businessLogicLog, collectErrorLogs, debug } = useStreamLogger("[CONSUME CONTROLLER]", getLoggerContext);
    WebRTC.logger.on("logging", ({ message, ...others }: LoggerContextWithMessage) =>
      debug(`[STREAM CLIENT] ${message}`, others)
    );

    const producer = computed(() => joinData.value?.users.find(i => i.userId === props.userId) ?? null);

    const dateStart = computed(() => {
      if (!producer.value) {
        return "";
      }
      return new Date(parseISO(producer.value.joinTime).getTime() + clientServerTimeDiff.value).toISOString();
    });

    watch(errorData, () => {
      if (!errorData) return;
      emit("error", errorData.value);
    });

    const consumeStream = async (broadcasterChatUserId: string) => {
      businessLogicLog("Consume stream");
      try {
        connectionData.value = await providers.LivechatsProvider.watch({
          token: token.value,
          broadcasterChatUserId,
        });
        WebRTC.closeAllStreams();
        stream.value = await WebRTC.consumeStream({
          connection: connectionData.value,
          tracks: { audio: true, video: true },
        });
      } catch (error) {
        collectErrorLogs("Consume stream", error);
      }
    };

    WebRTC.on("producers-updated", async ({ video, audio }: { video: boolean; audio: boolean }) => {
      if (!connectionData.value) {
        return;
      }
      businessLogicLog("Producers updating");
      try {
        stream.value = await WebRTC.consumeStream({
          connection: connectionData.value,
          tracks: { video, audio },
        });
      } catch (error) {
        collectErrorLogs("Producers updating", error);
      }
    });

    WebRTC.on("connectionstatechange", (state: string) => (connectingState.value = state));

    const closeStream = () => {
      businessLogicLog("Close stream");
      WebRTC.closeAllStreams();
      stream.value = new MediaStream([]);
    };

    setOnUserBroadcastingCB(({ chatUserId, broadcasting }) =>
      broadcasting ? consumeStream(chatUserId) : closeStream()
    );
    watch(joinData, (joinData, old) => {
      if (!joinData || (joinData && old)) return;
      if (producer.value && producer.value && producer.value.broadcasting) {
        consumeStream(producer.value.id);
      }
    });

    onUnmounted(closeStream);

    return () =>
      h(StreamView, {
        props: {
          produce: false,
          stream: unref(stream),
          connecting: unref(connecting),
          connectingState: unref(connectingState),
          loading: unref(loading),
          userList: unref(userList),
          streamStartedDate: unref(dateStart),
          threadId: joinData.value?.threadId,
          token: joinData.value?.token,
        },
        on: {
          close: listeners.close,
        },
      });
  },
});
